import { useEffect, useState } from 'react';

import cn from '@/utils/cn';
import styles from './TypewriterText.module.css';

const DEFAULT_SPEED_MS = 35;

interface TypewriterTextProps {
  text: string;
  enabled?: boolean;
  speed?: number;
  className?: string;
  onComplete?: () => void;
}

function TypewriterText({
  text,
  enabled = true,
  speed = DEFAULT_SPEED_MS,
  className,
  onComplete,
}: TypewriterTextProps) {
  const [visibleCount, setVisibleCount] = useState(enabled ? 0 : text.length);
  const [isComplete, setIsComplete] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setVisibleCount(text.length);
      setIsComplete(true);
      return undefined;
    }

    setVisibleCount(0);
    setIsComplete(false);

    if (!text.length) {
      setIsComplete(true);
      return undefined;
    }

    let count = 0;
    const intervalId = window.setInterval(() => {
      count += 1;
      setVisibleCount(count);

      if (count >= text.length) {
        window.clearInterval(intervalId);
        setIsComplete(true);
      }
    }, speed);

    return () => window.clearInterval(intervalId);
  }, [enabled, speed, text]);

  useEffect(() => {
    if (isComplete) onComplete?.();
  }, [isComplete, onComplete]);

  if (!enabled) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={cn([styles.root, className])}>
      {text.slice(0, visibleCount)}
      {!isComplete && <span className={styles.cursor} aria-hidden="true">|</span>}
    </span>
  );
}

export default TypewriterText;
